import styles from './boost-choice.module.scss';
import { Popin } from '@utmb/design-system/popin/Popin';
import { ButtonRemake } from '@utmb/design-system/button-remake/ButtonRemake';
import { Font } from '@utmb/design-system/text/font';
import { BoostStatusMessage } from '../boost-status-message/boost-status-message';
import { useTranslation } from 'next-i18next';
import { BoostCheckboxGroup } from '../boost-checkbox-group/boost-checkbox-group';
import { useRouter } from 'next/router';
import { BoostAdressReminder } from '../boost-adress-reminder/boost-adress-reminder';
import { ReactElement, useMemo, useState } from 'react';

interface BoostChoiceProps {
    callback: (value: boolean) => void;
    value?: boolean;
    percentage?: number;
    specialMode?: 'hasBoost' | 'needCustomCode';
}

export const BoostChoice = ({ callback, value, percentage, specialMode }: BoostChoiceProps): ReactElement => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState<boolean>(true);
    const [boxGroupStatus, setBoxGroupStatus] = useState<boolean>(false);

    const { locale } = useRouter();

    function getButtonStatus() {
        if (boxGroupStatus || specialMode) {
            return true;
        } else {
            return false;
        }
    }

    function handleBoxGroupStatusCallback(value: boolean) {
        setBoxGroupStatus(value);
    }

    function handleCallback(value: boolean) {
        setIsOpen(false);
        callback(value);
    }

    const wording = useMemo<BoostChoiceWording>(() => {
        if (specialMode) {
            if (specialMode === 'hasBoost') {
                return {
                    title: t('races.preregistration.boostPopin.localTitle', { pct: percentage }),
                    description: t('races.preregistration.boostPopin.localDescription'),
                    ctaAcept: t('races.preregistration.boostPopin.ctaCommit', { pct: percentage }),
                    ctaDecline: t('races.preregistration.boostPopin.editAdress'),
                    boostMessage: value ? t('races.preregistration.boostLocalReason') : undefined,
                };
            } else {
                return {
                    title: t('races.preregistration.localPopin.title'),
                    description: t('races.preregistration.localPopin.description'),
                    ctaAcept: t('races.preregistration.boostPopin.accept'),
                    ctaDecline: t('races.preregistration.boostPopin.editAdress'),
                    boostMessage: t('races.preregistration.localPopin.message'),
                };
            }
        } else {
            return {
                title: t('races.preregistration.boostPopin.title', { pct: percentage }),
                description: t('races.preregistration.boostPopin.description'),
                ctaAcept: t('races.preregistration.boostPopin.ctaCommit', { pct: percentage }),
                ctaDecline: t('races.preregistration.boostPopin.ctaWithout'),
                boostMessage: value ? t('races.preregistration.boostEngagementReason') : t('races.preregistration.noBoostReason'),
                list: t('races.preregistration.boostPopin.list'),
            };
        }
    }, [specialMode, value, t]);

    return (
        <div className={styles.boostChoise}>
            {specialMode === 'needCustomCode' ? (
                <Font.FuturaBook className={styles.label} desktop="16" mobile="16">
                    {wording.boostMessage}
                </Font.FuturaBook>
            ) : (
                <BoostStatusMessage
                    value={value}
                    label={wording.boostMessage}
                    percentage={percentage}
                    openCallback={specialMode ? undefined : () => setIsOpen(true)}
                />
            )}

            {isOpen ? (
                <Popin closeCallback={() => setIsOpen(false)} noCross isGrey>
                    <div className={styles.popin}>
                        <Font.FuturaBold desktop="24" mobile="24" className={styles.title}>
                            {wording.title}
                        </Font.FuturaBold>

                        <Font.FuturaBook className={styles.description}>
                            <span dangerouslySetInnerHTML={{ __html: wording.description }} />
                        </Font.FuturaBook>
                        {wording.list ? <div className={styles.wrapList} dangerouslySetInnerHTML={{ __html: wording.list }} /> : null}

                        {specialMode ? <BoostAdressReminder /> : <BoostCheckboxGroup callback={handleBoxGroupStatusCallback} />}
                        <div className={styles.wrapButtons}>
                            {specialMode ? (
                                <ButtonRemake
                                    customClassName={styles.decline}
                                    action={`https://utmb.world/${
                                        !locale || locale === 'en' || !['fr', 'it', 'es'].includes(locale) ? '' : `${locale}/`
                                    }my-utmb/my-information?modalOpen=contact&redirectUri=${encodeURIComponent(location.href)}`}
                                    label={wording.ctaDecline}
                                    variant="solid"
                                />
                            ) : (
                                <ButtonRemake
                                    customClassName={styles.decline}
                                    action={() => handleCallback(false)}
                                    label={wording.ctaDecline}
                                    variant="solid"
                                />
                            )}
                            <ButtonRemake
                                customClassName={styles.accept + ' ' + (getButtonStatus() ? '' : styles.disableButton)}
                                action={() => (getButtonStatus() ? handleCallback(true) : null)}
                                label={wording.ctaAcept}
                                variant="solid"
                            />
                        </div>
                    </div>
                </Popin>
            ) : null}
        </div>
    );
};

interface BoostChoiceWording {
    title: string;
    description: string;
    ctaAcept: string;
    ctaDecline: string;
    boostMessage?: string;
    list?: string;
}
